function changeHeaderCountry(isoCode) {
  if (!isoCode) return;
  const form = document.querySelector('#localization_form');
  const input = form?.querySelector("input[name='country_code']");
  if (!form || !input) return;
  input.value = isoCode;
  trackCountryPreference(isoCode);
  form.submit();
}

// help send EDMs according to time zone and language preference;
function trackCountryPreference(isoCode) {
  if (!isoCode || typeof customerEmail === 'undefined' || !customerEmail) return;

  const languageIso = window.Shopify?.localizationHVG?.languageIso ?? '';

  fetch('https://a.klaviyo.com/client/profiles/?company_id=Vd7qbT', {
    method: 'POST',
    headers: {
      accept: 'application/json',
      revision: '2024-07-15',
      'content-type': 'application/json',
    },
    body: JSON.stringify({
      data: {
        type: 'profile',
        attributes: {
          email: customerEmail,
          properties: {
            preferred_country: isoCode,
            preferred_lang: languageIso,
          },
        },
      },
    }),
  }).catch((error) => {
    console.error('Header country tracking error:', error);
  });
}

class SiteHeader extends HTMLElement {
  #controller = null;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    this.#initMenu(signal);
    this.#initBadges(signal);
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#controller = null;
  }

  #initMenu(signal) {
    const mobileMenu = this.querySelector('[data-mobile-menu]');
    const menuToggle = this.querySelector('[data-mobile-menu-toggle]');
    const menuOverlay = this.querySelector('[data-mobile-menu-overlay]');
    const menuMask = this.querySelector('[data-mobile-menu-mask]');

    if (!mobileMenu || !menuToggle || !menuOverlay) return;

    menuToggle.addEventListener('click', (event) => {
      event.preventDefault();
      if (mobileMenu.classList.contains('active')) {
        this.#closeMobileMenu(mobileMenu);
      } else {
        this.#openMobileMenu(mobileMenu);
      }
    }, { signal });

    menuOverlay.addEventListener('click', (event) => {
      if (event.target === menuOverlay) this.#closeMobileMenu(mobileMenu);
    }, { signal });

    menuMask?.addEventListener('click', () => this.#closeMobileMenu(mobileMenu), { signal });

    this.addEventListener('click', (event) => {
      const countryTrigger = event.target.closest('[data-country-iso]');
      if (countryTrigger && this.contains(countryTrigger)) {
        event.preventDefault();
        changeHeaderCountry(countryTrigger.dataset.countryIso);
        return;
      }

      const submenuTrigger = event.target.closest('.menutrigger[data-submenu_item]');
      if (!submenuTrigger || !this.contains(submenuTrigger)) return;

      const submenuLink = event.target.closest("a[href='#']");
      if (submenuLink) event.preventDefault();

      const submenuId = submenuTrigger.dataset.submenu_item;
      if (!submenuId) return;

      const currentPanel = submenuTrigger.closest('.level-2nd-holder');
      if (currentPanel && currentPanel.id === submenuId) {
        currentPanel.classList.remove('submenu-active');
        return;
      }

      const targetPanel = document.getElementById(submenuId);
      if (targetPanel && this.contains(targetPanel)) {
        targetPanel.classList.add('submenu-active');
      }
    }, { signal });
  }

  #initBadges(signal) {
    const wishlistTrigger = this.querySelector('[data-header-wishlist-trigger]');
    const cartTrigger = this.querySelector('[data-header-cart-trigger]');

    wishlistTrigger?.addEventListener('click', (event) => {
      event.preventDefault();
      window.dispatchEvent(new CustomEvent('enroute:cart-popup:open-wishlist'));
    }, { signal });

    cartTrigger?.addEventListener('click', (event) => {
      event.preventDefault();
      window.dispatchEvent(new CustomEvent('enroute:cart-popup:toggle'));
    }, { signal });

    this.#refreshBadges();

    window.addEventListener('enroute:cart-updated', () => this.#refreshBadges(), { signal });
    window.addEventListener('wishlist:updated', () => this.#refreshBadges(), { signal });
    window.addEventListener('load', () => this.#refreshBadges(), { signal });
  }

  #refreshBadges() {
    const cartState = this.#getCartState();
    const wishlistState = this.#getWishlistState();

    this.#updateBadge({
      container: this.querySelector('[data-header-cart]'),
      countNode: this.querySelector('[data-header-cart-count]'),
      count: cartState.count,
      hidden: false,
    });

    this.#updateBadge({
      container: this.querySelector('[data-header-wishlist]'),
      countNode: this.querySelector('[data-header-wishlist-count]'),
      count: wishlistState.count,
      hidden: wishlistState.hidden,
    });
  }

  #updateBadge({ container, countNode, count, hidden }) {
    if (!container || !countNode) return;

    const safeCount = Number.isFinite(count) ? count : 0;
    const hasItems = safeCount > 0;

    container.hidden = Boolean(hidden);
    container.classList.toggle('filled', hasItems);

    if (!hasItems || hidden) {
      countNode.textContent = '';
      countNode.hidden = true;
      countNode.classList.remove('two-digit');
      return;
    }

    countNode.textContent = String(safeCount);
    countNode.hidden = false;
    countNode.classList.toggle('two-digit', safeCount > 9);
  }

  #openMobileMenu(menuRoot) {
    this.#resetSubmenus(menuRoot);
    menuRoot.classList.add('active');
  }

  #closeMobileMenu(menuRoot) {
    menuRoot.classList.remove('active');
    this.#resetSubmenus(menuRoot);
  }

  #resetSubmenus(menuRoot) {
    for (const submenu of menuRoot.querySelectorAll('.submenu-active')) {
      submenu.classList.remove('submenu-active');
    }
  }

  #getCartState() {
    return {
      count: Number(
        window.enrouteCart?.state?.item_count ??
        (typeof cart !== 'undefined' ? cart?.item_count : undefined) ??
        window.cart?.item_count
      ) || 0,
    };
  }

  #getWishlistState() {
    const provider = window.wishlistProvider ?? null;
    if (!provider) return { count: 0, hidden: false };

    const hasError = Boolean(provider.storeObject?.error);
    const items = Array.isArray(provider.currentWishlist?.wishlistItems)
      ? provider.currentWishlist.wishlistItems
      : [];

    return { count: items.reduce((sum, item) => sum + (Number(item.quantity) || 0), 0), hidden: hasError };
  }
}

if (!customElements.get('site-header')) {
  customElements.define('site-header', SiteHeader);
}
